import {itemEmpty} from './common.js'
import itemUpdate from '../item-update.js'
import Vue from 'vue'

/**
 *
 * @param {object} base
 * @param {object} context
 * @return {{mutations: {}, state: {}, getters: {}, actions: {}, namespaced: boolean, modules}}
 */
export default function moduleEditItem (base, {parsed, properties}) {
  // FIXME: ensure we have needed actions and the like.

  // Reset to initial value.
  const RESET = 'RESET'
  // Set the initial values.
  const SET_ITEM = 'SET_ITEM'
  // Update the violations.
  const SET_VIOLATIONS = 'SET_VIOLATIONS'
  // Update the item.
  const UPDATE_ITEM = 'UPDATE_ITEM'

  // Validate the values against restrictions and return the list of violations.
  const validate = (values) => {
    const violations = {}
    Object.keys(properties).map((propName) => {
      if (required.indexOf(propName) !== -1 && (values[propName] === null || values[propName] === undefined)) {
        violations[propName] = 'This value should not be null.'
      }
    })
    return violations
  }

  const required = []
  Object.keys(properties).map((propName) => {
    const prop = properties[propName]
    if (prop.required) {
      required.push(propName)
    }
  })

  const result = {
    namespaced: true,
    state: {
      ...base.state,
      base: itemEmpty(properties),
      item: itemEmpty(properties),
      violations: null,
    },
    getters: {
      ...base.getters,
      item: state => state.item,
      violations: state => state.violations,
      // FIXME: this is only possible when we have information on props of "input" of put action and "output" of fetch action.
      readOnly: () => (propName) => !Object.keys(properties).some((property) => propName === property),
      required: () => (propName) => required.indexOf(propName) !== -1,
    },
    mutations: {
      ...base.mutations,
      [RESET] (state) {
        Object.assign(state, {
          item: Object.assign({}, state.base),
          violations: null
        })
      },
      [SET_VIOLATIONS] (state, violations) {
        Object.assign(state, { violations })
      },
      [SET_ITEM] (state, item) {
        Object.assign(state, {
          base: Object.assign({}, item),
          item
        })
      },
      [UPDATE_ITEM] (state, updated) {
        itemUpdate(state.item, updated, properties)
      }
    },
    actions: {
      ...base.actions,
      reset ({ commit, dispatch, getters, state }) {
        commit(RESET)
        commit(SET_VIOLATIONS, validate(state.item))
        return dispatch('resetSelections')
          .then(() => {
            return Object.assign({}, getters.item)
          })
      },
      setItem ({ commit, dispatch, getters }, data) {
        commit(SET_ITEM, data)
        commit(SET_VIOLATIONS, validate(data))
        return dispatch('updateSelections')
          .then(() => {
            return Object.assign({}, getters.item)
          })
      },
      updateItem ({ commit, dispatch, getters }, updated) {
        commit(UPDATE_ITEM, updated)
        commit(SET_VIOLATIONS, validate(getters.item))

        return new Promise((resolve, reject) => {
          dispatch('updateSelections')
            .then(() => {
              resolve()
            })
            .catch(() => {
              reject()
            })
        })
      },
      setViolations ({ commit }, violations) {
        commit(SET_VIOLATIONS, violations)

        return Promise.resolve(violations)
      },
      resetSelections () {
        return Promise.resolve()
      },
      updateSelections () {
        return Promise.resolve()
      }
    }
  }

  const referenceNames = []
  Object.keys(properties).map((propName) => {
    const reference = properties[propName].reference
    if (reference) {
      const resource = parsed[reference]
      if (resource && resource.operations.list && resource.operations.list.parameters.some((parameter) => parameter.name === 'orSearch_contains')) {
        referenceNames.push(propName)
      }
    }
  });

  if (referenceNames.length > 0) {
    const SET_SELECTED_ITEM = 'SET_SELECTED_ITEM_'
    const UPDATE_SEARCH = 'UPDATE_SEARCH_'
    const SET_OPTIONS = 'SET_OPTIONS_'
    result.actions.updateSelections = function ({dispatch, commit, getters}) {
      const promises = []
      referenceNames.map((propName) => {
        const listOperation = parsed[propName].operations.list
        promises.push(new Promise((resolve, reject) => {
          const id = getters.item[propName]
          // Test if id value is null...
          if (!id) {
            commit(SET_SELECTED_ITEM + propName, null)
            resolve()
            return
          }
          // ... or if selected value is already correct...
          const selected = getters[propName + '_selected']
          if (selected && selected['@id'] === id) {
              resolve()
              return
          }
          // ... or if it is in list of options.
          if (getters[propName + '_options'].some((item) => {
            if (item['@id'] === id) {
              commit(SET_SELECTED_ITEM + propName, item)
              return true
            }
          })) {
            resolve()
            return
          }
          const uri = listOperation.url + '?uuid=' + id.split('/').pop()
          dispatch('api/fetch', {uri}, { root: true })
            .then(response => response.json())
            .then((data) => {
              const options = data['hydra:member']
              commit(SET_SELECTED_ITEM + propName, options[0])
              resolve()
            })
            .catch((e) => {
              reject(e)
            })
        }))
      })
      return Promise.all(promises)
    }

    referenceNames.map((propName) => {
      const listOperation = parsed[propName].operations.list
      result.state = {
        ...result.state,
        [propName + '_options']: [],
        [propName + '_selected']: null,
        [propName + '_search']: '',
      }

      result.getters = {
        ...result.getters,
        [propName + '_options']: state => state[propName + '_options'],
        [propName + '_selected']: state => state[propName + '_selected'],
        [propName + '_search']: state => state[propName + '_search'],
      }

      result.mutations = {
        ...result.mutations,
        [SET_SELECTED_ITEM + propName] (state, selectedItem) {
          Vue.set(state, propName + '_selected', selectedItem)
        },
        [UPDATE_SEARCH + propName] (state, search) {
          Vue.set(state, propName + '_search', search)
        },
        [SET_OPTIONS + propName] (state, options) {
          Vue.set(state, propName + '_options', options)
        }
      }

      result.actions = {
        ...result.actions,
        // Selected element in list.
        ['search_' + propName] ({ commit, dispatch, state, rootGetters}, search) {
          commit(UPDATE_SEARCH + propName, search)
          const params = [
            'itemsPerPage=10'
          ]
          if (search) {
            params.push('&orSearch_contains=' + search)
          }
          listOperation.parameters.some((parameter) => {
            if (parameter.name.substring(0, 7) === 'company') {
              params.push(parameter.name + '=' + rootGetters['authentication/selectedCompanyId'])
              return true
            }
          })
          const uri = listOperation.url + '?' + params.join('&')
          return new Promise((resolve, reject) => {
            dispatch('api/fetch', {uri}, { root: true })
              .then(response => response.json())
              .then((data) => {
                commit(SET_OPTIONS + propName, data['hydra:member'])
                resolve()
              })
              .catch((e) => {
                reject(e)
              })
          })
        },
      }
    })
  }

  return result
}
